/**
 * IP-based country detection for the phone-signup country picker.
 *
 * Strategy: ask one fast HTTPS geo-IP service with a short timeout
 * and fall back to Brazil if it doesn't answer in time. This is a
 * "best effort" pre-fill; the user can always change the country
 * code from the picker before sending the SMS, so we never block
 * the form on this network call.
 *
 * Country dial codes are intentionally a small curated list — the
 * full list is huge and SMS-enabled regions are limited (Firebase
 * project's Phone Auth settings determine which actually work). The
 * list mirrors apps/web/components/EarlyAdopterSignup.tsx so users
 * who already used the marketing site see the same set.
 *
 * The number-shaping half lives in `@x9/phone-e164`, shared with every other
 * SMS gate so the same typed number is accepted everywhere.
 */
import {
  DEFAULT_DIAL_CODE as SHARED_DEFAULT_DIAL_CODE,
  isBrazilianMobile as sharedIsBrazilianMobile,
  isPlausiblePhone as sharedIsPlausiblePhone,
  phoneInputProblem as sharedPhoneInputProblem,
  toE164 as sharedToE164,
} from '@x9/phone-e164';

export type { PhoneInputProblem } from '@x9/phone-e164';

export interface CountryOption {
  /** E.164-style dial code with leading '+' (e.g. '+55') */
  dialCode: string;
  /** ISO 3166 alpha-2 country code (e.g. 'BR') for matching detector output */
  iso: string;
  /** Display label for the picker (e.g. 'BR') */
  label: string;
}

export const COUNTRY_OPTIONS: readonly CountryOption[] = Object.freeze([
  { dialCode: '+1', iso: 'US', label: 'US/CA' },
  { dialCode: '+55', iso: 'BR', label: 'BR' },
  { dialCode: '+44', iso: 'GB', label: 'GB' },
  { dialCode: '+49', iso: 'DE', label: 'DE' },
  { dialCode: '+33', iso: 'FR', label: 'FR' },
  { dialCode: '+39', iso: 'IT', label: 'IT' },
  { dialCode: '+34', iso: 'ES', label: 'ES' },
  { dialCode: '+31', iso: 'NL', label: 'NL' },
  { dialCode: '+32', iso: 'BE', label: 'BE' },
  { dialCode: '+41', iso: 'CH', label: 'CH' },
  { dialCode: '+43', iso: 'AT', label: 'AT' },
  { dialCode: '+46', iso: 'SE', label: 'SE' },
  { dialCode: '+47', iso: 'NO', label: 'NO' },
  { dialCode: '+45', iso: 'DK', label: 'DK' },
  { dialCode: '+358', iso: 'FI', label: 'FI' },
  { dialCode: '+351', iso: 'PT', label: 'PT' },
  { dialCode: '+353', iso: 'IE', label: 'IE' },
  { dialCode: '+48', iso: 'PL', label: 'PL' },
  { dialCode: '+420', iso: 'CZ', label: 'CZ' },
  { dialCode: '+36', iso: 'HU', label: 'HU' },
  { dialCode: '+40', iso: 'RO', label: 'RO' },
  { dialCode: '+7', iso: 'RU', label: 'RU' },
  { dialCode: '+81', iso: 'JP', label: 'JP' },
  { dialCode: '+86', iso: 'CN', label: 'CN' },
  { dialCode: '+91', iso: 'IN', label: 'IN' },
  { dialCode: '+52', iso: 'MX', label: 'MX' },
  { dialCode: '+54', iso: 'AR', label: 'AR' },
  { dialCode: '+61', iso: 'AU', label: 'AU' },
  { dialCode: '+64', iso: 'NZ', label: 'NZ' },
  { dialCode: '+27', iso: 'ZA', label: 'ZA' },
]);

/** Brazil is the primary market, so an unanswered lookup pre-fills +55. */
export const DEFAULT_DIAL_CODE = SHARED_DEFAULT_DIAL_CODE;

/**
 * Resolve the user's country to a dial code. Returns the default
 * ('+55') if detection fails, the country isn't in our curated list,
 * or the request times out — all "no answer" cases fall back rather
 * than throwing.
 *
 * Single service: ipwho.is is HTTPS, no API key, fast, generous quota.
 * If it ever stops working, the marketing-site component
 * (apps/web/components/EarlyAdopterSignup.tsx) has a 3-service race
 * pattern we can copy in.
 */
export async function detectDialCode(timeoutMs = 3000): Promise<string> {
  try {
    const response = await fetch('https://ipwho.is/', {
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!response.ok) return DEFAULT_DIAL_CODE;
    const data = (await response.json()) as { country_code?: string };
    const iso = data.country_code;
    if (!iso) return DEFAULT_DIAL_CODE;
    const match = COUNTRY_OPTIONS.find((c) => c.iso === iso);
    return match ? match.dialCode : DEFAULT_DIAL_CODE;
  } catch {
    // Timeout / offline / blocked — silently default.
    return DEFAULT_DIAL_CODE;
  }
}

/**
 * Strip non-digits and prepend the dial code, with the Brazilian rules: a
 * pasted country code is not doubled, a mobile typed without its 2016 ninth
 * digit is completed, and a number that reads as Brazilian stays on +55 even
 * if the picker guessed another country from the visitor's IP.
 *
 * This module used to do none of that, which made a mistyped Brazilian number
 * a silent failure: the send succeeded and the SMS never arrived.
 */
export const toE164 = sharedToE164;

/**
 * Loose validation — Firebase will reject anything malformed when we
 * actually call signInWithPhoneNumber, so this only guards against
 * obvious gibberish (empty, way too short, way too long).
 *
 * For Brazilian numbers prefer `phoneInputProblem`, which knows the numbering
 * plan and can say *why* a number will not work.
 */
export const isPlausiblePhone = sharedIsPlausiblePhone;

/** Why this number will not receive an SMS, or null when it should. */
export const phoneInputProblem = sharedPhoneInputProblem;

/** 11-digit national mobile: DDD + 9 + subscriber. */
export const isBrazilianMobile = sharedIsBrazilianMobile;
